import { computed } from '@angular/core';
import {
    signalStore,
    withState,
    withComputed,
    withMethods,
    patchState,
} from '@ngrx/signals';
import {
    ColumnFilterType,
    ViewType,
    ViewSearchConfig,
} from '@app/shared/models/view-config.interface';
import { PaginationInfo } from 'api-v3/models';
import { TableRowData } from '@app/shared/models/table-data-types';

/**
 * @deprecated Only still referenced by the legacy, never-rendered
 * `TableFilterComponent`. Column filters use {@link ActiveColumnFilter}.
 */
export interface FilterKeys {
    keyName: string;
    label: string;
}

/**
 * A column filter the user has applied. Several of these are AND-ed together by
 * the backend, and AND-ed with the full-text `searchText`.
 */
export interface ActiveColumnFilter {
    /** Backend query-param name. Also the identity of the filter. */
    keyName: string;
    /** Table column key, used to gray out the filtered column. */
    columnKey: string;
    /** Translation key for the chip label. */
    label: string;
    type: ColumnFilterType;
    /** Sent to the backend verbatim. Never empty. */
    value: string;
    /**
     * Translation key of the picked option's label, set for every option-based
     * filter. The chip shows this instead of the raw wire value (`'true'`,
     * `'open'`, `'self_checkin_driver_waiting'`, ...).
     */
    valueLabel?: string;
}

export interface PaginationState {
    offset: number;
    limit: number;
    hasMore: boolean;
    totalCount: number;
    /** true when the backend capped counting at 10 000 ("10.000+") */
    totalCountIsCapped: boolean;
}

export interface SortState {
    sortBy: // Common fields
        | 'dateCreated'
        | 'number'
        | 'orderNumber'
        | 'name'
        | 'status'
        | 'bundleNumber'
        | 'tourNumber'

        // Date fields
        | 'checkoutDate'
        | 'checkinDate'
        | 'dateAnnounced'
        | 'dateClosed'

        // Organization fields
        | 'consignorName'
        | 'consigneeName'
        | 'carrierName'
        | 'originalConsignor'
        | 'originalConsignorGln'

        // Delivery note specific
        | 'deliveryNoteBundleNumber'
        | 'deliveryNoteBundleStatus'

        // Carrier fields
        | 'carrier'
        | 'carrierNumberPlateOutgoing'
        | 'carrierNumberPlateIncoming'
        | 'consignorLoadCarrierExchange'

        // Tour fields
        | 'externalTourName'

        // Status fields
        | 'discrepancies'
        | 'palletTickets'

        // Properties
        | 'properties'
        | 'properties.referenceNumber'

        // Notification specific fields
        | 'eventType'
        | 'bundleNr'
        | 'organizationSiteKey'
        | null;
    sortDirection: 'asc' | 'desc';
}

export interface SearchState {
    searchText: string;
    columnFilters: ActiveColumnFilter[];
    pagination: PaginationState;
    sort: SortState;
    loading: boolean;
    currentViewType: ViewType;
    refreshTrigger: number;
    dataChangeSignal: {
        type: 'add' | 'update' | 'delete' | 'refresh';
        data?: TableRowData;
    };
}

export const initialSearchState: SearchState = {
    searchText: '',
    columnFilters: [],
    pagination: {
        offset: 0,
        limit: 10,
        hasMore: false,
        totalCount: 0,
        totalCountIsCapped: false,
    },
    sort: {
        sortBy: 'dateCreated',
        sortDirection: 'desc',
    },
    loading: false,
    currentViewType: 'folders',
    refreshTrigger: 0,
    dataChangeSignal: { type: 'refresh', data: undefined },
};

const SEARCH_DEBOUNCE_MS = 300;

export const SearchStore = signalStore(
    { providedIn: 'root' },
    withState<SearchState>(initialSearchState),
    withComputed(store => ({
        hasActiveFilters: computed(() => {
            return (
                store.searchText() !== '' || store.columnFilters().length > 0
            );
        }),
        /**
         * True for views that filter the already-loaded rows in the browser
         * instead of asking the backend - currently only notifications, see
         * `EventsPaginatedService.filteredEvents`. Their table is a computed
         * over {@link searchText} and {@link columnFilters}, so it re-renders on
         * its own and a reload would be pure cost: one request per typing pause
         * plus a loading flag that makes the search input read-only mid-word.
         */
        filtersAreClientSide: computed(
            () => store.currentViewType() === 'notifications'
        ),
        /**
         * True for views whose endpoint takes a single `filterProperty` /
         * `filterValue` pair and no full-text `search` - currently only bundle
         * groups, see `ConsigneeBundleGroupsPaginatedService`. A new column
         * filter replaces the previous one there and the search bar is hidden.
         */
        singleColumnFilterOnly: computed(
            () => store.currentViewType() === 'bundlegroups'
        ),
        /** Lookup for the table's per-column "is this column filtered" check. */
        activeFilterByColumnKey: computed(
            () =>
                new Map(
                    store.columnFilters().map(filter => [filter.columnKey, filter])
                )
        ),
        isFirstPage: computed(() => store.pagination().offset === 0),
        getCurrentPageIndex: computed(() => {
            const pagination = store.pagination();
            return Math.floor(pagination.offset / pagination.limit);
        }),
    })),
    withMethods(store => {
        // Private mutable variables for imperative side-effect state
        let currentAbortController: AbortController | null = null;
        let searchTextDebounceTimer: number | null = null;

        const triggerDataRefresh = () => {
            patchState(store, state => ({
                refreshTrigger: state.refreshTrigger + 1,
            }));
        };

        /**
         * Coalesces typing in the search bar and in any column filter popover
         * into a single reload. Called from the four filter mutators only, so
         * client-side views drop out here.
         */
        const scheduleDataRefresh = () => {
            if (store.filtersAreClientSide()) {
                return;
            }

            if (searchTextDebounceTimer !== null) {
                clearTimeout(searchTextDebounceTimer);
            }

            searchTextDebounceTimer = window.setTimeout(() => {
                triggerDataRefresh();
                searchTextDebounceTimer = null;
            }, SEARCH_DEBOUNCE_MS);
        };

        /** Cancels a pending debounce so a click-driven change refreshes at once. */
        const refreshImmediately = () => {
            if (store.filtersAreClientSide()) {
                return;
            }

            if (searchTextDebounceTimer !== null) {
                clearTimeout(searchTextDebounceTimer);
                searchTextDebounceTimer = null;
            }
            triggerDataRefresh();
        };

        const removeColumnFilter = (keyName: string): void => {
            if (!store.columnFilters().some(f => f.keyName === keyName)) {
                return;
            }

            patchState(store, state => ({
                columnFilters: state.columnFilters.filter(
                    f => f.keyName !== keyName
                ),
                pagination: { ...state.pagination, offset: 0 },
            }));
            refreshImmediately();
        };

        return {
            cancelOngoingRequests(): AbortSignal {
                if (
                    currentAbortController &&
                    !currentAbortController.signal.aborted
                ) {
                    currentAbortController.abort('New request initiated');
                }
                currentAbortController = new AbortController();
                return currentAbortController.signal;
            },

            triggerDataRefresh,

            updateSearchText(searchText: string): void {
                patchState(store, state => ({
                    searchText,
                    pagination: { ...state.pagination, offset: 0 },
                }));

                scheduleDataRefresh();
            },

            /**
             * Adds or replaces the filter for `filter.keyName`. An empty value
             * removes it instead - the backend treats `?key=` as a filter that
             * matches everything. Views with {@link singleColumnFilterOnly}
             * keep only the newest filter.
             */
            setColumnFilter(filter: ActiveColumnFilter): void {
                if (filter.value === '') {
                    removeColumnFilter(filter.keyName);
                    return;
                }

                patchState(store, state => {
                    if (store.singleColumnFilterOnly()) {
                        return {
                            columnFilters: [filter],
                            pagination: { ...state.pagination, offset: 0 },
                        };
                    }

                    const index = state.columnFilters.findIndex(
                        f => f.keyName === filter.keyName
                    );
                    const columnFilters = [...state.columnFilters];

                    if (index === -1) {
                        columnFilters.push(filter);
                    } else {
                        columnFilters[index] = filter;
                    }

                    return {
                        columnFilters,
                        pagination: { ...state.pagination, offset: 0 },
                    };
                });

                scheduleDataRefresh();
            },

            removeColumnFilter,

            clearColumnFilters(): void {
                if (store.columnFilters().length === 0) {
                    return;
                }

                patchState(store, state => ({
                    columnFilters: [],
                    pagination: { ...state.pagination, offset: 0 },
                }));
                refreshImmediately();
            },

            updatePagination(pagination: Partial<PaginationState>): void {
                patchState(store, state => ({
                    pagination: { ...state.pagination, ...pagination },
                }));
            },

            updatePaginationFromApiResponse(
                paginationInfo: PaginationInfo
            ): void {
                patchState(store, state => ({
                    pagination: {
                        ...state.pagination,
                        offset: paginationInfo.offset,
                        limit: paginationInfo.limit,
                        hasMore: paginationInfo.hasMore,
                        totalCount: paginationInfo.totalCount,
                        totalCountIsCapped:
                            paginationInfo.totalCountIsCapped === true,
                    },
                }));
                // NOTE: This deliberately does NOT call triggerDataRefresh() to prevent infinite loops
            },

            resetPaginationToEmpty(): void {
                patchState(store, state => ({
                    pagination: {
                        offset: 0,
                        limit: state.pagination.limit,
                        hasMore: false,
                        totalCount: 0,
                        totalCountIsCapped: false,
                    },
                }));
            },

            updateSort(
                sortBy: SearchState['sort']['sortBy'],
                sortDirection?: 'asc' | 'desc'
            ): void {
                const currentSort = store.sort();
                let newSort: SortState;

                if (currentSort.sortBy === sortBy) {
                    if (currentSort.sortDirection === 'asc') {
                        newSort = { sortBy, sortDirection: 'desc' };
                    } else if (currentSort.sortDirection === 'desc') {
                        if (sortBy === 'dateCreated') {
                            newSort = {
                                sortBy: 'dateCreated',
                                sortDirection: 'asc',
                            };
                        } else {
                            newSort = {
                                sortBy: 'dateCreated',
                                sortDirection: 'desc',
                            };
                        }
                    } else {
                        newSort = {
                            sortBy,
                            sortDirection: sortDirection || 'asc',
                        };
                    }
                } else {
                    newSort = {
                        sortBy,
                        sortDirection: sortDirection || 'asc',
                    };
                }

                patchState(store, state => ({
                    sort: newSort,
                    pagination: { ...state.pagination, offset: 0 },
                }));
            },

            setLoading(loading: boolean): void {
                patchState(store, { loading });
            },

            updateCurrentViewType(
                viewType: ViewType,
                viewSearchConfig?: ViewSearchConfig
            ): void {
                let sort: SortState;

                if (viewSearchConfig) {
                    sort = {
                        sortBy: viewSearchConfig.defaultSortField as SortState['sortBy'],
                        sortDirection: viewSearchConfig.defaultSortDirection,
                    };
                } else {
                    // Fallback defaults
                    if (
                        viewType === 'deliverynotes' ||
                        viewType === 'folders' ||
                        viewType === 'tours'
                    ) {
                        sort = {
                            sortBy: 'dateCreated',
                            sortDirection: 'desc',
                        };
                    } else {
                        sort = {
                            sortBy: 'dateCreated',
                            sortDirection: 'desc',
                        };
                    }
                }

                // Column filters are view-specific: carrying e.g. `bundleNumber`
                // over to the delivery-notes endpoint would fail with HTTP 400.
                patchState(store, state => ({
                    currentViewType: viewType,
                    pagination: { ...state.pagination, offset: 0 },
                    searchText: '',
                    columnFilters: [],
                    sort,
                }));
            },

            signalDataAdded(data: TableRowData): void {
                patchState(store, { dataChangeSignal: { type: 'add', data } });
                triggerDataRefresh();
            },

            signalDataUpdated(data: TableRowData): void {
                patchState(store, {
                    dataChangeSignal: { type: 'update', data },
                });
                triggerDataRefresh();
            },

            signalDataDeleted(data: TableRowData): void {
                patchState(store, {
                    dataChangeSignal: { type: 'delete', data },
                });
                triggerDataRefresh();
            },

            goToPage(offset: number): void {
                patchState(store, state => ({
                    pagination: { ...state.pagination, offset },
                }));
            },

            goToNextPage(): void {
                patchState(store, state => ({
                    pagination: {
                        ...state.pagination,
                        offset:
                            state.pagination.offset + state.pagination.limit,
                    },
                }));
            },

            goToPreviousPage(): void {
                patchState(store, state => ({
                    pagination: {
                        ...state.pagination,
                        offset: Math.max(
                            0,
                            state.pagination.offset - state.pagination.limit
                        ),
                    },
                }));
            },

            resetSearch(): void {
                patchState(store, {
                    searchText: initialSearchState.searchText,
                    columnFilters: initialSearchState.columnFilters,
                    pagination: initialSearchState.pagination,
                    sort: initialSearchState.sort,
                    loading: initialSearchState.loading,
                    currentViewType: initialSearchState.currentViewType,
                });
            },

            resetPagination(): void {
                patchState(store, state => ({
                    pagination: {
                        ...initialSearchState.pagination,
                        limit: state.pagination.limit,
                    },
                }));
            },
        };
    })
);
